import { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { addressAPI } from '../lib/api';
import { Loader2, MapPin } from 'lucide-react';

/**
 * Autosuggest input for French addresses (BAN API via backend proxy).
 *
 * Props:
 *  - value:        current Address Line 1 string
 *  - onChange:     (line1) => void  – fires on every keystroke
 *  - onSelect:     (suggestion) => void – fires when a suggestion is picked
 *                  suggestion = { line1, city, postal_code, state, country, label }
 *  - placeholder:  optional input placeholder
 *  - testId:       optional data-testid for the input
 *  - disabled
 */
export const AddressAutocomplete = ({
    value = '',
    onChange,
    onSelect,
    placeholder = 'Start typing an address...',
    testId,
    disabled = false,
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const wrapperRef = useRef(null);
    const skipNextFetch = useRef(false);

    // Debounced fetch
    useEffect(() => {
        if (skipNextFetch.current) {
            skipNextFetch.current = false;
            return;
        }
        const q = (value || '').trim();
        if (q.length < 3) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await addressAPI.autocomplete(q, 6);
                setSuggestions(Array.isArray(data) ? data : []);
                setOpen(true);
                setHighlight(-1);
            } catch {
                setSuggestions([]);
                setOpen(false);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [value]);

    // Close on outside click
    useEffect(() => {
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const pick = (s) => {
        skipNextFetch.current = true;
        setOpen(false);
        setSuggestions([]);
        onSelect?.(s);
    };

    const onKeyDown = (e) => {
        if (!open || suggestions.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === 'Enter' && highlight >= 0) {
            e.preventDefault();
            pick(suggestions[highlight]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div ref={wrapperRef} className="relative">
            <Input
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                onFocus={() => suggestions.length > 0 && setOpen(true)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                data-testid={testId}
                autoComplete="off"
            />
            {loading && (
                <Loader2
                    size={14}
                    className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"
                />
            )}
            {open && suggestions.length > 0 && (
                <div
                    className="absolute z-50 mt-1 left-0 right-0 bg-white border border-[#E5E5E5] rounded-md shadow-lg max-h-72 overflow-y-auto"
                    data-testid="address-suggestions"
                >
                    {suggestions.map((s, i) => (
                        <button
                            key={`${s.label}-${i}`}
                            type="button"
                            onClick={() => pick(s)}
                            onMouseEnter={() => setHighlight(i)}
                            data-testid={`address-suggestion-${i}`}
                            className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 border-b border-[#F5F5F5] last:border-0 ${
                                i === highlight ? 'bg-[#F5F5F5]' : 'bg-white hover:bg-[#FAFAFA]'
                            }`}
                        >
                            <MapPin size={14} className="text-[#C08478] mt-0.5 shrink-0" />
                            <span className="flex-1">
                                <span className="block text-[#1A1A1A]">{s.line1}</span>
                                <span className="block text-xs text-gray-500">
                                    {[s.postal_code, s.city].filter(Boolean).join(' ')}
                                    {s.state ? ` · ${s.state}` : ''}
                                </span>
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AddressAutocomplete;
